import { Box, Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Modal from ".";
import Title, { ERROR_HEADER } from "../custom/Title";

const DIMENSIONS = {
    width: 400,
    height: 300
}

const CANVAS_DIMENSIONS = {
    width: 800,
    height: 600
}

export interface CameraProps {
    title: string;
    open: boolean;
    clearOnSave: boolean;
    b64Image?: string;
    closeHandler: () => any;
    saveImage: (imageDataURL: string) => any;
}

function Camera (props: CameraProps) {
    const { title, open, b64Image, clearOnSave, closeHandler, saveImage } = props;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [ imageUrl, setImageUrl ] = useState<string | undefined>(b64Image);
    const [ videoStream, setVideoStream ] = useState<MediaStream | undefined>(undefined);
    const [ isCameraPermissionDenied, setIsCameraPermissionDenied ] = useState(false);

    useEffect(() => {
        if(open) {
            openCamera();
        } else {
            closeCamera();
        }

        return () => {
            closeCamera();
        };
    }, [open]);

    const openCamera = () => {
        navigator.mediaDevices.getUserMedia({ video: true }).then(stream => {
            setVideoStream(stream);
            setIsCameraPermissionDenied(false);
            if(videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
        }).catch(err => {
            if(err.message === 'Permission denied'){
                setIsCameraPermissionDenied(true);
            }
        });
    };

    const closeCamera = () => {
        if(videoStream){
            videoStream.getTracks().forEach(track => {
                track.stop();
            });
            setVideoStream(undefined);
        }
    };

    const takeImage = () => {
        const context = canvasRef.current?.getContext("2d");
        if (context && videoRef.current) {
            context.drawImage(videoRef.current, 0, 0, CANVAS_DIMENSIONS.width, CANVAS_DIMENSIONS.height);
            const imageData = canvasRef.current?.toDataURL("image/png", 1);
            setImageUrl(imageData);
            closeCamera();
        }
    };

    const retakeImage = () => {
        setImageUrl(undefined);
        openCamera();
    };

    const captureImage = () => {
        saveImage((imageUrl as string));
        closeHandler();
        if(clearOnSave) {
          setImageUrl(undefined);
        }
    }

    return (
      <>
        <Modal open={open} onClose={closeHandler} title={title}>
          {isCameraPermissionDenied && !imageUrl ? (
            <Title style={ERROR_HEADER}>
              Please provide permission to access camera.
            </Title>
          ) : (
            <Box>
              <canvas
                ref={canvasRef}
                width={CANVAS_DIMENSIONS.width}
                height={CANVAS_DIMENSIONS.height}
                style={{ display: "none" }}
              ></canvas>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  width={DIMENSIONS.width}
                  height={DIMENSIONS.height}
                />
              ) : (
                <video
                  ref={videoRef}
                  width={DIMENSIONS.width}
                  height={DIMENSIONS.height}
                ></video>
              )}
              <Box>
                {imageUrl ? (
                  <>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={retakeImage}
                    >
                      Retake Photo
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={captureImage}
                      style={{ marginLeft: "5px" }}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={takeImage}
                  >
                    Take Photo
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Modal>
      </>
    );
}

export default Camera;
